import { z } from "zod";
import { MCP_TRANSPORT_TYPES, MCP_STATUSES, MCP_AUTH_TYPES, MCP_SECRET_SCOPES } from "../constants.js";
// --- Auth config sub-schemas ---
export const mcpAuthConfigSchema = z.object({
    type: z.enum(MCP_AUTH_TYPES).default("none"),
    // api_key
    apiKeyHeader: z.string().max(200).optional(),
    apiKeyValue: z.string().max(2000).optional(),
    // bearer
    bearerToken: z.string().max(4000).optional(),
    // oauth_client_credentials
    oauthTokenUrl: z.string().url().max(2000).optional(),
    oauthClientId: z.string().max(500).optional(),
    oauthClientSecret: z.string().max(2000).optional(),
    oauthScope: z.string().max(500).optional(),
    // oauth_signin — all the live state lives in the mcp_oauth_connections
    // table (keyed by the catalogue MCP id). The auth config itself only
    // needs the discriminator + an optional explicit scope override the
    // admin can request at sign-in time. No secrets are stored here.
    oauthSigninScope: z.string().max(1000).optional(),
});
// --- HTTP / SSE config ---
export const httpConfigSchema = z.object({
    url: z.string().url().max(2000),
    headers: z.record(z.string()).optional(),
    auth: mcpAuthConfigSchema.optional(),
});
// --- Stdio config ---
export const stdioConfigSchema = z.object({
    command: z.string().min(1).max(500),
    args: z.array(z.string().max(500)).optional(),
    env: z.record(z.string()).optional(),
});
// --- Catalogue CRUD ---
export const createMcpCatalogueSchema = z.object({
    // null or omitted = platform-wide; set = tenant-owned (visible to that
    // tenant + MSP admins, mutable by that tenant's admins).
    companyId: z.string().uuid().nullable().optional(),
    name: z.string().min(1).max(100),
    displayName: z.string().max(100).optional(),
    description: z.string().max(1000).optional(),
    transportType: z.enum(MCP_TRANSPORT_TYPES),
    icon: z.string().max(50).optional(),
    httpConfig: z.record(z.unknown()).optional(),
    stdioConfig: z.record(z.unknown()).optional(),
    secretScope: z.enum(MCP_SECRET_SCOPES).optional(),
    version: z.string().max(20).optional(),
    // Platform-managed MCPs only (ignored for tenant-owned). When true, the
    // MCP is visible to every tenant. When false, visibility is restricted
    // to tenants listed in `allocatedCompanyIds`.
    isAvailableToAll: z.boolean().optional(),
    allocatedCompanyIds: z.array(z.string().uuid()).optional(),
});
export const updateMcpCatalogueSchema = z.object({
    companyId: z.string().uuid().nullable().optional(),
    name: z.string().min(1).max(100).optional(),
    displayName: z.string().max(100).nullable().optional(),
    description: z.string().max(1000).nullable().optional(),
    transportType: z.enum(MCP_TRANSPORT_TYPES).optional(),
    icon: z.string().max(50).nullable().optional(),
    httpConfig: z.record(z.unknown()).nullable().optional(),
    stdioConfig: z.record(z.unknown()).nullable().optional(),
    secretScope: z.enum(MCP_SECRET_SCOPES).optional(),
    status: z.enum(MCP_STATUSES).optional(),
    version: z.string().max(20).nullable().optional(),
    isAvailableToAll: z.boolean().optional(),
    allocatedCompanyIds: z.array(z.string().uuid()).optional(),
});
// --- Tenant assignment ---
export const assignTenantMcpSchema = z.object({
    companyId: z.string().uuid(),
    catalogueMcpId: z.string().uuid(),
    name: z.string().min(1).max(100).optional(),
    customConfig: z.record(z.unknown()).optional(),
    credentialsVault: z.string().max(500).optional(),
    allowedTools: z.array(z.string().min(1).max(200)).optional(),
});
export const updateTenantMcpSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    customConfig: z.record(z.unknown()).nullable().optional(),
    credentialsVault: z.string().max(500).nullable().optional(),
    allowedTools: z.array(z.string().min(1).max(200)).nullable().optional(),
    enabled: z.boolean().optional(),
});
// --- Agent assignment ---
export const assignAgentMcpSchema = z.object({
    agentId: z.string().uuid(),
    tenantMcpId: z.string().uuid(),
    configOverrides: z.record(z.unknown()).optional(),
    allowedTools: z.array(z.string().min(1).max(200)).optional(),
});
export const updateAgentMcpSchema = z.object({
    configOverrides: z.record(z.unknown()).nullable().optional(),
    allowedTools: z.array(z.string().min(1).max(200)).nullable().optional(),
    enabled: z.boolean().optional(),
});
//# sourceMappingURL=mcp.js.map